import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, ShieldCheck, ArrowRight, LockKey, Fingerprint, Keyboard,
} from "@phosphor-icons/react";
import {
  startAuthentication,
} from "@simplewebauthn/browser";
import { staffApi, formatStaffErr, getDeviceId } from "../../lib/staffApi";
import { useStaffAuth } from "../../lib/staffAuth";

/**
 * Field-Staff Mobile Login (iter 46 rewrite).
 *
 * Four unlock methods available on the same screen:
 *   1. OTP       (Phone + one-time code)          — always available
 *   2. Password  (Phone + password)               — requires password to be set
 *   3. PIN       (Phone + 4-digit PIN)            — requires PIN enrolled on device
 *   4. Biometric (device passkey via WebAuthn)    — requires biometric enrolled on device
 *
 * The device_id is stable per browser (localStorage), so a returning user
 * automatically sees PIN + Biometric options if they've enrolled them.
 */
export default function StaffLogin() {
  const { staff, login, loginWithToken } = useStaffAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [step, setStep] = useState("phone"); // phone → chooser → otp | password | pin
  const [mode, setMode] = useState("otp");   // otp | password | pin | biometric
  const [device, setDevice] = useState({ has_password: false, has_pin: false, has_biometric: false });
  const [loading, setLoading] = useState(false);

  if (staff) return <Navigate to="/staff-app" replace />;

  const probeDevice = async (ph) => {
    try {
      const r = await staffApi.post("/mobile-auth/device/status", {
        user_type: "staff", identifier: ph, device_id: getDeviceId(),
      });
      setDevice({
        has_password: !!r.data.has_password,
        has_pin: !!r.data.has_pin,
        has_biometric: !!r.data.has_biometric,
      });
      // Prefer biometric > PIN > password > OTP for a returning user
      const preferred = r.data.has_biometric ? "biometric"
        : r.data.has_pin ? "pin"
        : r.data.has_password ? "password"
        : "otp";
      setMode(preferred);
      setStep("chooser");
    } catch {
      setDevice({ has_password: false, has_pin: false, has_biometric: false });
      setMode("otp");
      setStep("chooser");
    }
  };

  const submitPhone = async (e) => {
    e.preventDefault();
    if (!/^\d{6,15}$/.test(phone.trim())) { toast.error("Enter a valid phone"); return; }
    setLoading(true);
    await probeDevice(phone.trim());
    setLoading(false);
  };

  // ── OTP path ──
  const sendOtp = async () => {
    setLoading(true);
    try {
      const r = await staffApi.post("/public/staff/login/otp", { phone: phone.trim() });
      setDevOtp(r.data.dev_otp || "");
      setStep("otp");
      toast.success("OTP sent");
    } catch (e) { toast.error(formatStaffErr(e)); }
    setLoading(false);
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error("Enter 6-digit OTP"); return; }
    setLoading(true);
    try {
      await login(phone.trim(), otp.trim());
      toast.success("Signed in");
      nav("/staff-app");
    } catch (e) { toast.error(formatStaffErr(e)); }
    setLoading(false);
  };

  // ── Password path ──
  const submitPassword = async (e) => {
    e.preventDefault();
    if (!password) { toast.error("Enter your password"); return; }
    setLoading(true);
    try {
      const r = await staffApi.post("/mobile-auth/password-login", {
        user_type: "staff",
        identifier: phone.trim(),
        password,
        device_id: getDeviceId(),
      });
      loginWithToken(r.data.token, {
        ...r.data.user, tenant_id: r.data.user.tenant_id, mobile_auth: r.data.device,
      });
      toast.success("Signed in");
      nav("/staff-app");
    } catch (e) { toast.error(formatStaffErr(e)); }
    setLoading(false);
  };

  // ── PIN path ──
  const submitPin = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) { toast.error("Enter 4-digit PIN"); return; }
    setLoading(true);
    try {
      const r = await staffApi.post("/mobile-auth/pin/unlock", {
        user_type: "staff",
        identifier: phone.trim(),
        device_id: getDeviceId(),
        pin,
      });
      loginWithToken(r.data.token, r.data.user);
      toast.success("Unlocked");
      nav("/staff-app");
    } catch (e) { toast.error(formatStaffErr(e)); }
    setLoading(false);
  };

  // ── Biometric path (WebAuthn assertion) ──
  const submitBiometric = async () => {
    setLoading(true);
    try {
      const chal = await staffApi.post("/mobile-auth/biometric/unlock/challenge", {
        user_type: "staff",
        identifier: phone.trim(),
        device_id: getDeviceId(),
      });
      const options = typeof chal.data.options === "string" ? JSON.parse(chal.data.options) : chal.data.options;
      const assertion = await startAuthentication({ optionsJSON: options });
      const r = await staffApi.post("/mobile-auth/biometric/unlock/verify", {
        challenge_id: chal.data.challenge_id,
        credential: assertion,
        user_type: "staff",
        identifier: phone.trim(),
        device_id: getDeviceId(),
      });
      loginWithToken(r.data.token, r.data.user);
      toast.success("Unlocked with biometric");
      nav("/staff-app");
    } catch (e) {
      const msg = e?.name === "NotAllowedError"
        ? "Biometric cancelled"
        : formatStaffErr(e) || e?.message || "Biometric failed";
      toast.error(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0F1F0C] text-white flex flex-col" data-testid="staff-login">
      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <img src="/brand/rescigo-mark-128.png" alt="Rescigo"
                  className="w-12 h-12 rounded-md object-contain bg-white p-1.5 ring-1 ring-white/20"/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/60">Rescigo · Field app</div>
              <div className="font-heading text-xl leading-tight">Staff sign-in</div>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur rounded-lg border border-white/10 p-6" data-testid="staff-login-card">
            {step === "phone" && (
              <form onSubmit={submitPhone} className="space-y-4" data-testid="staff-login-phone-form">
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-1">
                  Your phone
                </div>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                  <input data-testid="staff-phone" value={phone}
                          onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                          placeholder="9000000003" autoFocus inputMode="numeric"
                          className="w-full h-12 pl-10 pr-3 bg-white/10 border border-white/15 rounded-md text-[15px] font-mono text-white placeholder-white/40 focus:outline-none focus:border-[#D4E0D1]/60"/>
                </div>
                <button data-testid="staff-continue" disabled={loading} type="submit"
                         className="w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                  {loading ? "Checking…" : "Continue"} <ArrowRight size={14} weight="bold" />
                </button>
                <div className="text-xs text-white/50 text-center">
                  Your device unlocks with OTP, password, PIN, or biometric.
                </div>
              </form>
            )}

            {step === "chooser" && (
              <div className="space-y-3" data-testid="staff-login-chooser">
                <div className="text-sm text-white/70">
                  Signing in as <span className="font-mono font-medium text-white">{phone}</span>
                  <button onClick={() => { setStep("phone"); setDevOtp(""); }}
                           className="ml-2 text-[#D4E0D1] text-xs underline" data-testid="staff-change-phone">
                    change
                  </button>
                </div>

                <UnlockTabs mode={mode} setMode={setMode} device={device}/>

                {mode === "otp" && (
                  <button onClick={sendOtp} disabled={loading} data-testid="staff-send-otp"
                           className="w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                    {loading ? "Sending…" : "Send OTP"} <ArrowRight size={14} weight="bold" />
                  </button>
                )}

                {mode === "password" && (
                  <form onSubmit={submitPassword} className="space-y-3" data-testid="staff-login-pw-form">
                    <div className="relative">
                      <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                      <input data-testid="staff-password" type="password" value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              placeholder="Password" autoFocus
                              className="w-full h-12 pl-10 pr-3 bg-white/10 border border-white/15 rounded-md text-[14px] text-white placeholder-white/40"/>
                    </div>
                    <button data-testid="staff-pw-submit" disabled={loading} type="submit"
                             className="w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium">
                      {loading ? "…" : "Sign in with password"}
                    </button>
                    {!device.has_password && (
                      <div className="text-[11px] text-[#F9E4B7]">
                        No password set yet for this account. Use OTP once, then set a password.
                      </div>
                    )}
                  </form>
                )}

                {mode === "pin" && (
                  <form onSubmit={submitPin} className="space-y-3" data-testid="staff-login-pin-form">
                    <div className="relative">
                      <Keyboard size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                      <input data-testid="staff-pin" value={pin} inputMode="numeric" maxLength={4}
                              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                              placeholder="4-digit PIN" autoFocus
                              className="w-full h-12 pl-10 pr-3 bg-white/10 border border-white/15 rounded-md text-center text-[18px] font-mono tracking-[0.4em] text-white"/>
                    </div>
                    <button data-testid="staff-pin-submit" disabled={loading || pin.length !== 4} type="submit"
                             className="w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium">
                      {loading ? "…" : "Unlock with PIN"}
                    </button>
                    {!device.has_pin && (
                      <div className="text-[11px] text-[#F9E4B7]">
                        No PIN enrolled on this device. Sign in with OTP or password first, then set a PIN.
                      </div>
                    )}
                  </form>
                )}

                {mode === "biometric" && (
                  <div className="space-y-3" data-testid="staff-login-biometric">
                    <button onClick={submitBiometric} disabled={loading || !device.has_biometric}
                             data-testid="staff-bio-submit"
                             className="w-full h-14 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                      <Fingerprint size={22} weight="fill" />
                      {loading ? "Waiting…" : "Unlock with biometric"}
                    </button>
                    {!device.has_biometric && (
                      <div className="text-[11px] text-[#F9E4B7]">
                        No biometric enrolled on this device. Sign in another way first, then enrol biometric from Settings.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === "otp" && (
              <form onSubmit={verifyOtp} className="space-y-4" data-testid="staff-login-otp-form">
                <div className="text-sm text-white/70">
                  Code sent to <span className="font-mono font-medium text-white">{phone}</span>
                  <button type="button" onClick={() => { setStep("chooser"); setOtp(""); }}
                           className="ml-2 text-[#D4E0D1] text-xs underline">back</button>
                </div>
                {devOtp && (
                  <div className="text-[11px] bg-[#F9E4B7]/15 border border-[#F9E4B7]/30 rounded p-2 text-[#F9E4B7]">
                    DEV OTP: <span className="font-mono font-semibold">{devOtp}</span>
                  </div>
                )}
                <div className="relative">
                  <ShieldCheck size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                  <input data-testid="staff-otp" value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          placeholder="6-digit code" autoFocus inputMode="numeric" maxLength={6}
                          className="w-full h-12 pl-10 pr-3 bg-white/10 border border-white/15 rounded-md text-center text-[18px] font-mono tracking-[0.4em] text-white"/>
                </div>
                <button data-testid="staff-verify-otp" disabled={loading || otp.length !== 6} type="submit"
                         className="w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium">
                  {loading ? "Verifying…" : "Sign in"}
                </button>
              </form>
            )}
          </div>

          <div className="mt-6 text-center text-xs text-white/50">
            Not staff? <Link to="/me/login" className="text-[#D4E0D1] underline">Customer app</Link> ·{" "}
            <Link to="/login" className="text-[#D4E0D1] underline">Admin</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const UnlockTabs = ({ mode, setMode, device }) => {
  const tabs = [
    { id: "biometric", label: "Biometric", Icon: Fingerprint, disabled: !device.has_biometric },
    { id: "pin", label: "PIN", Icon: Keyboard, disabled: !device.has_pin },
    { id: "password", label: "Password", Icon: LockKey, disabled: !device.has_password },
    { id: "otp", label: "OTP", Icon: ShieldCheck, disabled: false },
  ].filter((t) => !t.disabled || t.id === mode);
  return (
    <div className="grid grid-cols-4 gap-1 bg-white/5 border border-white/10 rounded-md p-1" data-testid="staff-unlock-tabs">
      {tabs.map(({ id, label, Icon, disabled }) => (
        <button key={id} onClick={() => setMode(id)} disabled={disabled}
                data-testid={`staff-tab-${id}`}
                className={`h-11 rounded-sm text-[11px] inline-flex flex-col items-center justify-center gap-0.5 transition-colors ${
                  mode === id ? "bg-[#2D5A27] text-white" : "text-white/60 hover:bg-white/5 disabled:opacity-30 disabled:pointer-events-none"
                }`}>
          <Icon size={13} weight={mode === id ? "fill" : "regular"} />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
};
